import React, { useEffect, useRef } from 'react'

import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { ExitToApp } from '@mui/icons-material'
import { Drawer, List } from '@mui/material'
import logoHeader from 'assets/svg/header/logo-havaianas.svg'
import { usePermissions } from 'commons/customHooks/useAuth'
import { type Notification, useNotification } from 'commons/customHooks/useNotifications'
import { useWindowSize } from 'commons/customHooks/useWindowSize'
import i18n, { TranslateComponent } from 'commons/libs/i18n'
import { formatDate } from 'commons/utils/date'
import { downloadFile, FileType } from 'commons/utils/file'
import { EnvironmentTag, Icon, Modal, Notifications, ProfileMenu, Typography } from 'components'
import { URL_CONST } from 'constants/urls'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { downloadRecommendationsReport } from 'services/home'
import { handleLogout } from 'store/AuthContext/authSlice'
import { useDispatch } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import { LABEL_PAGE, navItems, SUPPORT_EMAIL, SUPPORT_LINK } from './constants'
import * as S from './styles'

// NOTE: THIS COMPONENT IS TO BE REMOVED IN THE FUTURE, AS IT IS BEING REPLACED BY MenuTopbar AND MenuSidebar COMPONENTS
// TODO: REMOVE COMPONENT AND MOVE HEADER CONSTANTS TO APPROPRIATE LOCATIONS AFTER NEW COMPONENTS ARE FULLY IMPLEMENTED AND IN USE

export const Header: React.FC = () => {
  const appInsights = useAppInsightsContext()
  const [openMenu, setOpenMenu] = React.useState(false)
  const [openSubMenu, setOpenSubMenu] = React.useState(false)
  const [openSupportModal, setOpenSupportModal] = React.useState(false)
  const [openRecommendationModal, setOpenRecommendationModal] = React.useState(false)
  const [openNotificationsDrawer, setOpenNotificationsDrawer] = React.useState(false)
  const [openNotifications, setOpenNotifications] = React.useState(false)
  const [width] = useWindowSize()
  const notifButtonRef = useRef<HTMLDivElement>(null)
  const matchSMScreen = width < theme.breakpoints.lg
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { verifyPermission } = usePermissions()
  const { processUnreadNotifications, notificationsData, unreadNotificationsData } =
    useNotification()

  const toggleMenuDrawer = () => {
    setOpenMenu(!openMenu)
    setOpenNotificationsDrawer(false)
    setOpenSubMenu(false)
  }

  const toggleSubMenu = () => {
    setOpenSubMenu(!openSubMenu)
  }

  const toggleNotificationsDrawer = () => {
    if (openNotificationsDrawer) {
      processUnreadNotifications()
    }
    setOpenNotificationsDrawer(!openNotificationsDrawer)
    setOpenMenu(false)
    setOpenSubMenu(false)
  }
  const toggleNotifications = () => {
    if (openNotifications) {
      processUnreadNotifications()
    }
    setOpenNotifications(!openNotifications)
  }

  const goHome = () => {
    navigate(URL_CONST.ROOT)
    goHomeHandler()
  }

  const goHomeHandler = () => {
    window.scrollTo({ behavior: 'smooth', top: 0 })
  }

  const logout = () => {
    navigate(URL_CONST.LOGIN)
    logoutHandler()
  }

  const logoutHandler = () => {
    appInsights.trackEvent({ name: 'Logout Manual' })
    dispatch(handleLogout())
  }

  const isRouteActive = (route: string) => {
    return route === location.pathname
  }

  const onClickHandler = (label: string) => {
    if (label === LABEL_PAGE.RECOMMENDATION) {
      setOpenRecommendationModal(true)
    }
  }

  const verifyActiveRouteItems = (routeItems: Array<{ label: string; route: string }>) => {
    return routeItems.some((value) => isRouteActive(value.route))
  }

  useEffect(() => {
    if (!matchSMScreen) {
      setOpenNotificationsDrawer(false)
      setOpenMenu(false)
    }
  }, [matchSMScreen, setOpenNotificationsDrawer, setOpenMenu])

  const renderButtonsContent = () => (
    <S.ButtonsItemsStyled>
      <S.IconButtonStyled onClick={() => setOpenSupportModal(true)}>
        <Icon color={theme.colors.neutral.white} icon="headset" size="20" />
      </S.IconButtonStyled>

      {openNotificationsDrawer ? (
        <S.IconButtonStyled onClick={toggleNotificationsDrawer}>
          <S.CloseIconStyled />
        </S.IconButtonStyled>
      ) : (
        <>
          <S.NotificationsButtonWrapper ref={notifButtonRef}>
            <S.IconButtonStyled
              onClick={matchSMScreen ? toggleNotificationsDrawer : toggleNotifications}
            >
              <Icon color={theme.colors.neutral.white} icon="notification" size="24" />
            </S.IconButtonStyled>
            {!!unreadNotificationsData.length && (
              <S.NotificationNumberStyled>
                {unreadNotificationsData.length < 9 ? unreadNotificationsData.length : '9+'}
              </S.NotificationNumberStyled>
            )}
          </S.NotificationsButtonWrapper>
          {openNotifications && (
            <Notifications notifButtonRef={notifButtonRef} setOpen={setOpenNotifications} />
          )}
        </>
      )}

      {!matchSMScreen && (
        <S.ProfileMenuWrapper>
          <ProfileMenu onLogout={logout} />
        </S.ProfileMenuWrapper>
      )}
    </S.ButtonsItemsStyled>
  )

  const handleRecommendationReportDownload = async () => {
    toast.info('O download começará em instantes.')
    setOpenRecommendationModal(false)

    try {
      const file = await downloadRecommendationsReport()
      //NOTE: Ex: Recomendacao_Pedidos_YYYYMMDD_HHMM.xlsx
      const fileName = (() => {
        // NOTE: If necessary in the future, this format "YYYYMMDD_HHMM" can be added to DateFormats in the date.ts file
        // *if the format files name becomes the standard format
        const date = new Date(),
          value = (n: number) => String(n).padStart(2, '0')
        // TODO: Use i18n for file name
        return `Recomendacao_Pedidos_${date.getFullYear()}${value(
          date.getMonth() + 1,
        )}${value(date.getDate())}_${value(date.getHours())}${value(date.getMinutes())}.xlsx`
      })()

      downloadFile({
        file,
        fileName,
        fileType: FileType.Excel,
      })
      // TODO: Use i18n for toast message
      toast.success('Planilha baixada com sucesso.')
    } catch (error) {
      // TODO: Use i18n for toast message
      toast.error('Erro ao baixar a planilha. Verifique sua conexão e tente novamente.')
    }
  }

  return (
    <>
      <S.AppBarStyled
        height={60}
        position="fixed"
        sx={{ zIndex: (themeStyle) => themeStyle.zIndex.drawer + 1 }}
      >
        <S.WrapContainerStyled>
          <S.WrapGridContainerStyled>
            <S.RowDiv>
              <S.RowDiv>
                {matchSMScreen && (
                  <S.IconButtonStyled onClick={toggleMenuDrawer}>
                    {openMenu ? <S.CloseIconStyled /> : <S.MenuIconStyled />}
                  </S.IconButtonStyled>
                )}
                {!matchSMScreen && (
                  <S.LogoHeader alt="Logo Header Havaianas" onClick={goHome} src={logoHeader} />
                )}
              </S.RowDiv>
              {!matchSMScreen && (
                <S.RowDiv isNavItems>
                  {Object.keys(navItems).map(function (key, index) {
                    if (!verifyPermission(navItems[key].screen, navItems[key].action)) return
                    return (
                      <S.RowDiv key={`row-${index}${key}`}>
                        {navItems[key].item.length > 0 ? (
                          <S.Dropdown>
                            <S.NavItemStyled
                              active={verifyActiveRouteItems(navItems[key].item).toString()}
                              className="submenu-label"
                              key={`label-${index}`}
                              to={''}
                              type={S.TYPES_NAV_ITEM.PRIMARY}
                            >
                              {navItems[key].label}
                              <S.ChevronDownIconStyled />
                            </S.NavItemStyled>
                            <S.DropdownContent className="dropdown-content">
                              {navItems[key]?.item?.map(
                                ({ route, label, screen }, indexNavItem) => {
                                  if (!verifyPermission(screen)) return
                                  return (
                                    <S.NavItemStyled
                                      active={isRouteActive(route).toString()}
                                      key={`label-${indexNavItem}`}
                                      onClick={() => onClickHandler(label)}
                                      to={route}
                                      type={S.TYPES_NAV_ITEM.SECONDARY}
                                    >
                                      {label}
                                    </S.NavItemStyled>
                                  )
                                },
                              )}
                            </S.DropdownContent>
                          </S.Dropdown>
                        ) : (
                          <S.NavItemStyled
                            active={isRouteActive(navItems[key].route).toString()}
                            key={`label-${index}`}
                            to={navItems[key].route}
                            type={S.TYPES_NAV_ITEM.PRIMARY}
                          >
                            {navItems[key].label}
                          </S.NavItemStyled>
                        )}
                      </S.RowDiv>
                    )
                  })}
                </S.RowDiv>
              )}
            </S.RowDiv>
            {matchSMScreen && (
              <S.RowDiv>
                <S.LogoHeader alt="Logo Header Havaianas" onClick={goHome} src={logoHeader} />
              </S.RowDiv>
            )}

            <S.WrapSubContainer>
              <EnvironmentTag />
              {renderButtonsContent()}
            </S.WrapSubContainer>
          </S.WrapGridContainerStyled>
        </S.WrapContainerStyled>
      </S.AppBarStyled>
      <Drawer anchor="left" open={openMenu}>
        <S.WrapDrawerContent width={300}>
          <S.EmptySpaceDivider isSubmenu={openSubMenu} />
          <List disablePadding>
            {Object.keys(navItems).map(function (key, index) {
              if (!verifyPermission(navItems[key].screen, navItems[key].action)) return

              return (
                <React.Fragment key={`column-${index}`}>
                  {!openSubMenu ? (
                    <S.ColumnDiv key={`column-${index}`}>
                      <>
                        {navItems[key]?.item?.length > 0 ? (
                          <>
                            <S.ListItemStyled onClick={toggleSubMenu}>
                              {navItems[key].label}
                              <S.ChevronRightIconStyled />
                            </S.ListItemStyled>
                          </>
                        ) : (
                          <S.DrawerNavItemStyled
                            active={isRouteActive(navItems[key].route).toString()}
                            key={`label-${index}`}
                            to={navItems[key].route}
                          >
                            {navItems[key].label}
                          </S.DrawerNavItemStyled>
                        )}
                      </>
                    </S.ColumnDiv>
                  ) : (
                    <>
                      {navItems[key]?.item?.length > 0 && (
                        <>
                          <S.ColumnDiv key={`column-${index}`}>
                            <S.ListItemStyled active onClick={toggleSubMenu}>
                              <S.ChevronLeftIconStyled />
                              {navItems[key].label}
                            </S.ListItemStyled>

                            {navItems[key]?.item?.map(({ route, label }, innerIndex) => {
                              return (
                                <>
                                  <S.DrawerNavItemStyled
                                    active={isRouteActive(route).toString()}
                                    key={`label-${innerIndex}`}
                                    to={route}
                                  >
                                    {label}
                                  </S.DrawerNavItemStyled>
                                </>
                              )
                            })}
                          </S.ColumnDiv>
                        </>
                      )}
                    </>
                  )}
                </React.Fragment>
              )
            })}
            <S.DividerStyled />
            <S.EmptySpaceDivider />
            <S.DrawerLogoutButton onClick={logout}>
              <ExitToApp sx={{ fontSize: '20px' }} />
              Sair
            </S.DrawerLogoutButton>
          </List>
        </S.WrapDrawerContent>
      </Drawer>
      <Drawer anchor="right" open={openNotificationsDrawer}>
        <S.WrapDrawerContent width={496}>
          <S.DrawerHeaderStyled>
            <S.SideBarTitleStyled>Notificações</S.SideBarTitleStyled>
          </S.DrawerHeaderStyled>
          <S.DividerStyled />
          <S.WrapDrawerNotifications>
            {notificationsData.length > 0 ? (
              notificationsData.map((element: Notification, index) => (
                <div className="row" key={`row-${index}`}>
                  <div id="date" key={`row-date-${index}`}>
                    {formatDate(element.create_date, 'DD/MM')}
                  </div>
                  <div id="message" key={`row-message-${index}`}>
                    {element.message}
                  </div>
                </div>
              ))
            ) : (
              <S.EmptyNotificationsStyled className="row">
                <Icon color={theme.colors.neutral.white} icon="notification-off" size="24" />
                Sem notificações.
              </S.EmptyNotificationsStyled>
            )}
          </S.WrapDrawerNotifications>
        </S.WrapDrawerContent>
      </Drawer>
      <Modal
        description={
          <Typography design="hav-default-03-text">
            <TranslateComponent
              components={{
                br: <br key="br" />,
                click_here: (
                  <S.SupportLink
                    href={SUPPORT_LINK}
                    key="click_here"
                    rel="noreferrer noopener"
                    target="_blank"
                  />
                ),
                email: (
                  <S.SupportLink
                    href={`mailto:${SUPPORT_EMAIL}`}
                    key="email"
                    rel="noreferrer noopener"
                    target="_blank"
                  />
                ),
                strong: <strong key="strong" />,
              }}
              i18nKey="components.header.supportModal.description"
              values={{ supportEmail: SUPPORT_EMAIL }}
            />
          </Typography>
        }
        open={openSupportModal}
        setOpen={setOpenSupportModal}
        showAction={false}
        title={i18n.t('components.header.supportModal.title')}
        titleSize={22}
      />

      <Modal
        buttonAction={handleRecommendationReportDownload}
        buttonIcon={<Icon icon="download" size="16" />}
        buttonText="Baixar"
        closeAction={() => setOpenRecommendationModal(false)}
        description={
          <Typography design="hav-default-03-text">
            <strong>Baixe nossa planilha de recomendação</strong> e confira uma lista personalizada
            de produtos sugeridos com base no seu histórico de pedidos.
          </Typography>
        }
        open={openRecommendationModal}
        returnText="Voltar"
        setOpen={setOpenRecommendationModal}
        showReturn
        title="Recomendação"
        titleIcon="table"
        titleSize={20}
      />
    </>
  )
}
